import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { enpointCervinoWeb } from 'src/app/configuration/enpointsCervino';
import { CostAdjustmentDetailModel } from 'src/app/models/inventory/cost-adjustment-detail.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { ArticleSelectionByEntryTableModel } from 'src/app/models/tables/archive/article-selection-by-entry-table.model';
import { CostAdjustmentDetailCaptureTableModel } from 'src/app/models/tables/inventory/cost-adjustment-detail-capture-table.model';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class CostAdjustmentDetailService {

  constructor(private _httpClient: HttpClient) { }

  createCostAdjustmentDetail(costAdjustmentDetailContract: CostAdjustmentDetailModel): Observable<CostAdjustmentDetailModel> {
    return this._httpClient.post<CostAdjustmentDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.costAdjustmentDetail.CreateCostAdjustmentDetailAsync}`,
      costAdjustmentDetailContract
    );
  }

  createSomeDetails(costAdjustmentDetails: CostAdjustmentDetailModel[]): Observable<boolean> {
    return this._httpClient.post<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.costAdjustmentDetail.CreateDetailsAsync}`, costAdjustmentDetails);
  }

  deleteCostAdjustmentDetail(costAdjustmentDetailId: number): Observable<CostAdjustmentDetailModel> {
    const params = new HttpParams().set('costAdjustmentDetailId', costAdjustmentDetailId);
    return this._httpClient.delete<CostAdjustmentDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.costAdjustmentDetail.DeleteCostAdjustmentDetailAsync}`,
      { params: params }
    );
  }

  deleteSomeDetails(costAdjustmentDetailIds: number[]): Observable<boolean> {
    return this._httpClient.put<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.costAdjustmentDetail.DeleteSomeDetailsAsync}`,
      costAdjustmentDetailIds
    );
  }

  exportDetailConsultation(filters: ColumnResponseModel[], columnSort: string | null, ascendant: boolean): Observable<Blob> {
    const params = new HttpParams().set('columnSort', columnSort ?? '').set('ascendant', ascendant.toString());
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.costAdjustmentDetail.ExportDetailConsultation}`, filters, {
      params, responseType: 'blob',
    });
  }

  getAllCostAdjustmentDetailsWithPagination(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<any>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);
    return this._httpClient.post<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.costAdjustmentDetail.GetAllCostAdjustmentDetailsWithPaginationAsync}`,
      filters, { params: params }
    );
  }

  getArticlesByEntry(inventoryEntryId: number, page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<ArticleSelectionByEntryTableModel>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant).set('inventoryEntryId', inventoryEntryId);
    return this._httpClient.post<PagedResponseModel<ArticleSelectionByEntryTableModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.costAdjustmentDetail.GetArticlesByEntryAsync}`,
      filters, { params: params }
    );
  }

  getColumnsForArticle(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.costAdjustmentDetail.GetColumnsForArticleAsync}`);
  }

  getColumnsForCaptureTable(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.costAdjustmentDetail.GetColumnsForCaptureTableAsync}`);
  }

  getColumnsForDetailConsultation(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.costAdjustmentDetail.GetColumnsForDetailConsultationAsync}`);
  }

  getCostAdjustmentDetailById(costAdjustmentDetailId: number): Observable<CostAdjustmentDetailModel> {
    const params = new HttpParams().set('costAdjustmentDetailId', costAdjustmentDetailId);
    return this._httpClient.get<CostAdjustmentDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.costAdjustmentDetail.GetCostAdjustmentDetailByIdAsync}`,
      { params: params }
    );
  }

  getCostAdjustmentDetailByKeys(costAdjustmentId: number, articleId: number): Observable<CostAdjustmentDetailModel> {
    const params = new HttpParams().set('costAdjustmentId', costAdjustmentId).set('articleId', articleId);
    return this._httpClient.get<CostAdjustmentDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.costAdjustmentDetail.GetCostAdjustmentDetailByKeysAsync}`,
      { params: params }
    );
  }

  getCostAdjustmentDetailsForCaptureTable(page: number, limit: number, columnSort: string | null, ascendant: boolean, costAdjustmentId: number): Observable<PagedResponseModel<CostAdjustmentDetailCaptureTableModel>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant).set('costAdjustmentId', costAdjustmentId);
    return this._httpClient.get<PagedResponseModel<CostAdjustmentDetailCaptureTableModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.costAdjustmentDetail.GetCostAdjustmentDetailsForCaptureTableAsync}`,
      { params: params }
    );
  }

  updateCostAdjustmentDetail(costAdjustmentDetailContract: CostAdjustmentDetailModel): Observable<CostAdjustmentDetailModel> {
    return this._httpClient.put<CostAdjustmentDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.costAdjustmentDetail.UpdateCostAdjustmentDetailAsync}`,
      costAdjustmentDetailContract
    );
  }
}
